import { WorkspaceCardData } from "../components/Interfaces/WorkspaceCard";
import { BookingStatus, BookingType } from "../constants-generated/well-known-values";

// Interface for booking data from API
export interface BookingData {
  id: number;
  userId: number;
  startDate: string;
  endDate: string;
  totalPrice: number;
  status: string;
  bookingType: number; // 0 = Reserve, 1 = Tour
  message?: string;
  workspaceId: number;
  workspaceTitle: string;
  workspaceDescription: string;
  workspaceAddress: string;
  workspaceCity: string;
  workspaceState: string;
  workspacePostalCode: string;
  workspaceCountry: string;
  workspacePricePerYear: number;
  workspaceCapacity: number;
  workspaceSizeSqm: number;
  workspaceType: string;
  workspaceImageUrl: string;
  paymentStatus?: string;
}

// Map BookingData to WorkspaceCardData for reusing WorkspaceCard component
export const mapBookingToWorkspaceCard = (booking: BookingData): WorkspaceCardData => {
  return {
    workspaceId: booking.workspaceId,
    title: booking.workspaceTitle,
    description: booking.workspaceDescription,
    address: booking.workspaceAddress,
    city: booking.workspaceCity,
    state: booking.workspaceState,
    postalCode: booking.workspacePostalCode,
    country: booking.workspaceCountry,
    pricePerYear: booking.workspacePricePerYear,
    capacity: booking.workspaceCapacity,
    sizeSqm: booking.workspaceSizeSqm,
    workspaceType: booking.workspaceType,
    images: booking.workspaceImageUrl ? [{
      imageUrl: booking.workspaceImageUrl,
      deleteUrl: '' // Not needed for display purposes
    }] : [],
    amenities: [], // Bookings don't include amenity details
    success: true,
    message: ""
  };
};

// Utility to get booking status badge color
export const getBookingStatusColor = (status: string): string => {
  switch (status) {
    case BookingStatus.Confirmed:
      return 'bg-green-100 text-green-800';
    case BookingStatus.Pending:
      return 'bg-yellow-100 text-yellow-800';
    case BookingStatus.Cancelled:
      return 'bg-red-100 text-red-800';
    case BookingStatus.Completed:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Utility to get booking type text
export const getBookingTypeText = (bookingType: number): string => {
  return bookingType === BookingType.Reservation ? BookingType.ReservationText : BookingType.TourText;
};

// Utility to format date range
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

// Utility to check if booking is upcoming (not yet ended)
export const isUpcomingBooking = (endDate: string): boolean => {
  const end = new Date(endDate);
  const now = new Date();
  // Set time to start of day for fair comparison
  now.setHours(0, 0, 0, 0);
  return end >= now;
};

// Utility to check if booking is past (already ended)
export const isPastBooking = (endDate: string): boolean => {
  const end = new Date(endDate);
  const now = new Date();
  // Set time to start of day for fair comparison
  now.setHours(0, 0, 0, 0);
  return end < now;
};